import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminEnrollments } from '../../api/enrollments';
import { getAdminEditions } from '../../api/courses';
import StatusBadge from '../../components/StatusBadge';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import type { EnrollmentStatus } from '../../types';

const STATUS_COUNTS: Array<{ status: EnrollmentStatus; label: string; color: string; bg: string }> = [
  { status: 'portal_registered',        label: 'Registrate', color: '#475569', bg: '#f1f5f9' },
  { status: 'zoom_registration_pending', label: 'In attesa',  color: '#b45309', bg: '#fef9ee' },
  { status: 'zoom_approved',            label: 'Approvate',  color: '#166534', bg: '#f0fdf4' },
  { status: 'attended',                 label: 'Presenti',   color: '#1d4ed8', bg: '#eff6ff' },
  { status: 'no_show',                  label: 'Assenti',    color: '#b91c1c', bg: '#fff1f2' },
  { status: 'cancelled',                label: 'Annullate',  color: '#6b7280', bg: '#f9fafb' },
];

export default function AdminDashboard() {
  const { data: enrollmentsData, isLoading: isLoadingEnrollments } = useQuery({
    queryKey: ['admin-enrollments', 'dashboard'],
    queryFn: () => getAdminEnrollments({ limit: 100 }),
    refetchInterval: 60_000,
  });

  const { data: editions = [], isLoading: isLoadingEditions } = useQuery({
    queryKey: ['admin-editions'],
    queryFn: getAdminEditions,
  });

  const enrollments = enrollmentsData?.enrollments ?? [];

  // Count by status
  const statusCounts = STATUS_COUNTS.map((item) => ({
    ...item,
    count: enrollments.filter((e) => e.status === item.status).length,
  }));

  // Total enrolled (non-cancelled)
  const totalActive = enrollments.filter((e) => e.status !== 'cancelled').length;

  // Recent enrollments (last 10)
  const recentEnrollments = [...enrollments]
    .sort((a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime())
    .slice(0, 10);

  // Upcoming editions
  const now = new Date();
  const upcomingEditions = [...editions]
    .filter((e) => new Date(e.scheduledAt) > now)
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
    .slice(0, 5);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>
          {format(new Date(), "EEEE d MMMM yyyy", { locale: it })}
        </span>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card" style={{ background: '#eff6ff' }}>
          <div className="stat-value" style={{ color: '#1d4ed8' }}>{totalActive}</div>
          <div className="stat-label">Iscrizioni attive</div>
        </div>
        {statusCounts.map((item) => (
          <div
            key={item.status}
            className="stat-card"
            style={{ background: item.bg }}
          >
            <div className="stat-value" style={{ color: item.color }}>
              {item.count}
            </div>
            <div className="stat-label">{item.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Recent enrollments */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title">Iscrizioni recenti</h3>
              <Link to="/admin/enrollments" className="btn btn-ghost btn-sm">
                Vedi tutte
              </Link>
            </div>
          </div>

          {isLoadingEnrollments ? (
            <div className="loading-container" style={{ padding: '2rem' }}>
              <div className="spinner" />
            </div>
          ) : recentEnrollments.length === 0 ? (
            <p className="text-muted text-sm">Nessuna iscrizione</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Corso</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {recentEnrollments.map((e) => (
                    <tr key={e.id}>
                      <td>
                        <Link
                          to={`/admin/enrollments/${e.id}`}
                          style={{ fontWeight: 500, color: 'var(--primary)' }}
                        >
                          {e.user?.nome} {e.user?.cognome}
                        </Link>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
                          {e.user?.email}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.875rem' }}>
                          {e.edition?.course?.name ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                          {e.edition?.label}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={e.status as EnrollmentStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Upcoming editions */}
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title">Prossime edizioni</h3>
              <Link to="/admin/editions" className="btn btn-ghost btn-sm">
                Vedi tutte
              </Link>
            </div>
          </div>

          {isLoadingEditions ? (
            <div className="loading-container" style={{ padding: '2rem' }}>
              <div className="spinner" />
            </div>
          ) : upcomingEditions.length === 0 ? (
            <p className="text-muted text-sm">Nessuna edizione pianificata</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {upcomingEditions.map((edition) => (
                <div
                  key={edition.id}
                  style={{
                    padding: '0.875rem',
                    border: '1px solid var(--gray-200)',
                    borderRadius: 'var(--border-radius)',
                    background: 'var(--gray-50)',
                  }}
                >
                  <div style={{ fontWeight: 600, color: 'var(--gray-800)', marginBottom: '0.25rem' }}>
                    {edition.course?.name ?? '—'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.375rem' }}>
                    {edition.label}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--primary)' }}>
                      {format(new Date(edition.scheduledAt), "d MMM yyyy 'ore' HH:mm", { locale: it })}
                    </span>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
                      {edition.enrollmentCount ?? 0}/{edition.maxParticipants} posti
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
